// External dependencies.
import { LitElement, html, css, unsafeCSS } from "lit";

// Internal dependencies.
import { sharedStyles } from "./styles/shared-styles";

/**
 * AppNav component class.
 *
 * @since 0.1.0
 * @class AppNav
 * @extends {LitElement}
 */
class AppNav extends LitElement {
    /**
     * Creates an instance of AppNav.
     * 
     * @since 0.1.0
     * @memberof AppNav
     */
    constructor() {
        super();
        this.mainMenuTemplate = null;
        this.removeTop = false;
        this.toggleSubmenu = this.toggleSubmenu.bind( this );
    }

    /**
     * Inits the component's properties.
     *
     * @since 0.1.0
     * @readonly
     * @static
     * @memberof AppNav
     */
    static get properties() {
        return {
            removeTop: { type: Boolean },
            isOpen: { type: Boolean },
        };
    }

    /**
     * Inits the component's CSS styles.
     *
     * @since 0.1.0
     * @readonly
     * @static
     * @memberof AppNav
     */
    static get styles() {
        const navTop = AppNav.navTop();

        return [ sharedStyles, css`
            .ts-nav {
                z-index: 1031;
                position: fixed;
                top: ${unsafeCSS( navTop )}px;
                right: 0;
                bottom: 0;
                left: 0;
                width: 100%;
                padding-top: 5.763rem;
                padding-right: 15px;
                padding-left: 15px;
                background-color: white;
                overflow: auto;
                transform: translateX(100%);
                transition: transform .4s linear;
            }

            .ts-nav--no-top {
                top: 0 !important;
            }

            .ts-nav--open {
                transform: translateX(0);
                box-shadow: -.250rem 0 .250rem 0px rgba(0, 0, 0, .1);
            }

            .ts-main-menu {
                list-style: none;
                padding: 0;
                margin: 0;
            }

            .ts-main-menu ul {
                list-style: none;
            }

            .ts-main-menu a {
                color: var(--color-body);
            }

            .ts-main-menu > .menu-item > a {
                display: block;
                padding: .25rem 0;
                border-bottom: .063rem solid var(--color-body);
                font-size: 1.5rem;
                font-family: var(--font-gabarito);
                font-weight: bold;
            }

            .ts-main-menu .sub-menu {
                display: block;
                padding-top: .25rem;
                font-size: 1.125rem;
            }

            .ts-main-menu .expanded > .sub-menu {
                display: block;                
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu {
                padding-left: 0;
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu > li::before {
                display: none;
                content: "\\2013";
                padding-right: .5rem;
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu > li {
                margin-left: 10px !important;
                border-bottom: .063rem solid var(--color-body);
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu > li > a {
                font-size: 1.3rem;
            }

            .btn-on-menu > a {
                border: 2px solid #535152;
                font-size: 1rem !important;
                text-align: center;
                margin: 15px 0;
                border-radius: 30px;
                padding: 15px !important;
                transition: all .3s ease-in-out;
            }

            .btn-on-menu > a:hover {
                background-color: #fecc46 !important;
                border-color: #fecc46 !important; 
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu > li.ts-menu-btn-primary::before {
                content: "";
                padding-right: 0;
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu > li.ts-menu-btn-primary > a {
                display: inline-block;
                font-weight: 700;
                color: var(--color-body);
                text-align: center;
                vertical-align: middle;
                -webkit-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
                background-color: var(--color-primary);
                border: .125rem solid var(--color-primary);
                padding: 0.25rem 0.5rem;
                margin-top: .75rem;
                font-size: 0.875rem;
                line-height: 1.5;
                border-radius: 50rem;
                text-transform: uppercase;
                transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu > li.ts-menu-btn-primary > a:hover {
                color: var(--color-body) !important;
                background-color: var(--color-primary-dark) !important;
                border-color: var(--color-primary-dark) !important;
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu .sub-menu {
                padding-left: 1.5rem;
            }

            .ts-main-menu > .menu-item-has-children > .sub-menu > .menu-item-has-children > .sub-menu li::before {
                content: "\\2666";
                padding-right: .5rem;
                color: var(--color-body-dark);
            }

            @media (min-width: 768px) {
                .ts-nav {
                    left: auto;
                    max-width: 21.875rem;
                    padding-top: 8.5rem;
                }
            }

            @media (min-width: 1200px) {
                .ts-nav {
                    padding-top: 10.375rem;
                }
            }
        ` ];
    }

    /**
     * Returns the CSS top property for the .ts-nav element.
     *
     * @since 0.1.0
     * @static
     * @returns
     * @memberof AppNav
     */
    static navTop() {
        let navTop = 0;

        if ( window.app.is_admin_bar_showing ) {
            navTop = ( window.matchMedia( "(min-width: 783px)" ).matches ) ? 32 : 46;
        }

        return navTop;
    }

    /**
     * Runs when the component is appended to the document.
     *
     * @since 0.1.0
     * @listens event:resize
     * @listens event:scroll
     * @memberof AppNav
     */
    connectedCallback() {
        super.connectedCallback();
        this.mainMenuTemplate = document.querySelector( '#ts-main-menu-template' );

        window.addEventListener( 'resize', () => {
            const navTop = AppNav.navTop();
            this.shadowRoot.querySelector( '.ts-nav' ).style.top = `${navTop}px`;
        } );

        window.addEventListener( 'scroll', () => {
            const currentScrollPosition = window.pageYOffset;

            if ( window.app.is_admin_bar_showing && window.matchMedia( "(max-width: 600px)" ).matches ) {
                this.removeTop = ( 1 <= currentScrollPosition ) ? true : false;
            }
        } );
    }

    /**
     * Runs when the component has been updated and rendered.
     *
     * @since 0.1.0
     * @since 1.1.0 Additional submenu opens by default.
     * @since 1.1.1 Updated submenu selectors.
     * @listens event:click
     * @memberof AppNav
     */
    updated() {
        this.submenuTogglers = this.shadowRoot.querySelectorAll( '.menu-item-has-children > a' );
        !!this.submenuTogglers.length && this.submenuTogglers.forEach( elem => {
            elem.addEventListener( 'click', this.toggleSubmenu );
        } );
    }

    /**
     * Toggles submenus.
     *
     * @since 0.1.0
     * @param object e
     * @memberof AppNav
     */
    toggleSubmenu( e ) {
        e.preventDefault();
        const liElement = e.target.closest( 'li' );
        liElement.classList.toggle( 'expanded' );
    }

    /**
     * Renders the component.
     *
     * @since 0.1.0
     * @returns TemplateResult
     * @memberof AppNav
     */
    render() {
        return html`
            <nav class="ts-nav${this.isOpen ? ' ts-nav--open' : ''}${this.removeTop ? ' ts-nav--no-top' : ''}">${this.mainMenuTemplate ? this.mainMenuTemplate.content.cloneNode( true ) : null}</nav>
        `;
    }
}

window.customElements.define( 'app-nav', AppNav );
